import { CLIMBING_STYLES } from './constants.js';
import {
  convertDecimalEwbanksToFrench,
  convertDecimalEwbanksToYDS,
  findGradeByFrench,
  findGradeByUIAA,
  findGradeByYDS
} from '../data/grades.js';
import { 
  generateFractionalGradeTable,
  findAverageVGrade,
  vGradeToNumber
} from './boulderGrades.js';

import {
  CLEAN_SPORT_ASCENTS,
  CLEAN_BOULDER_ASCENTS,
  FAILED_ASCENTS
} from './ascentTypes.js';

// Replace existing constants
export const SPORT_CLEAN_ASCENT_TYPES = CLEAN_SPORT_ASCENTS;
export const BOULDER_CLEAN_ASCENT_TYPES = CLEAN_BOULDER_ASCENTS;
export const FAILED_ASCENT_TYPES = FAILED_ASCENTS;

// Grade conversion mappings
export const french_grades = {
  1: "1a", 2: "1a+/b", 3: "1b+", 4: "1c/+", 5: "2a/+", 6: "2b", 7: "2b+", 8: "2c/+",
  9: "3a", 10: "3a+/b", 11: "3b+/c", 12: "3c+", 13: "4a/+", 14: "4b/+", 15: "4c/+",
  16: "5a-b", 17: "5b+/c", 18: "5c+/6a", 19: "6a+", 20: "6b", 21: "6b+/6c", 22: "6c/+",
  23: "7a", 24: "7a+", 25: "7b", 26: "7b+", 27: "7c", 28: "7c+", 29: "8a", 30: "8a+",
  31: "8b", 32: "8b+", 33: "8c", 34: "8c+", 35: "9a", 36: "9a+", 37: "9b", 38: "9b+",
  39: "9c"
};

export const yds_grades = {
  4: "5.0", 5: "5.1", 6: "5.1/5.2", 7: "5.2", 8: "5.2/5.3", 9: "5.3", 10: "5.4",
  11: "5.4/5.5", 12: "5.5", 13: "5.6", 14: "5.7", 15: "5.7/5.8", 16: "5.8", 17: "5.9",
  18: "5.10a", 19: "5.10b", 20: "5.10c/d", 21: "5.11a", 22: "5.11b", 23: "5.11c/d",
  24: "5.12a", 25: "5.12b", 26: "5.12c", 27: "5.12d", 28: "5.13a", 29: "5.13b",
  30: "5.13c", 31: "5.13d", 32: "5.14a", 33: "5.14b", 34: "5.14c", 35: "5.14d",
  36: "5.15a", 37: "5.15b", 38: "5.15c", 39: "5.15d"
};

// Visual styling
export const customColors = {
  onsight: '#22c55e',   // green
  flash: '#f98326',     // orange
  redpoint: '#ef4444',  // red
  pinkpoint: '#f472b6',  // Using Tailwind's pink-400 instead of pink-500
  send: '#3b82f6',      // blue for boulder sends
  repeat: '#8b5cf6',    // purple for boulder repeats
  attempts: '#aaaaaa',  // gray
  progressMax: '#22c55e',
  progressAvg: '#22d3ee',
  progressRolling: '#f97316'
};

// Ascent type styling
export const getAscentTypeColor = (type) => {
  switch (type) {
    case 'Onsight':
      return customColors.onsight;
    case 'Flash':
      return customColors.flash;
    case 'Red point':
    case 'First ascent':
      return customColors.redpoint;
    case 'Pink point':
      return customColors.pinkpoint;
    case 'Send':
      return customColors.send;
    case 'Repeat':
      return customColors.repeat;
    default:
      return customColors.attempts;
  }
};

export const convertEwbanksToFrench = (ewbanks) => {
  if (ewbanks === null || isNaN(ewbanks)) return null;
  
  // If it's an integer (or very close to one), use the simple mapping
  if (Math.abs(Math.round(ewbanks) - ewbanks) < 0.001) {
    return french_grades[Math.round(ewbanks)] || null;
  }
  
  // For decimal grades, use the precise conversion from grades.js
  return convertDecimalEwbanksToFrench(ewbanks);
};

export const convertEwbanksToYDS = (ewbanks) => {
  if (ewbanks === null || isNaN(ewbanks)) return null;
  
  // If it's an integer (or very close to one), use the simple mapping
  if (Math.abs(Math.round(ewbanks) - ewbanks) < 0.001) {
    return yds_grades[Math.round(ewbanks)] || null;
  }
  
  // For decimal grades, use the precise conversion from grades.js
  return convertDecimalEwbanksToYDS(ewbanks);
};

const getAverageEwbank = (matches) => {
  if (!matches || matches.length === 0) return NaN;
  const sum = matches.reduce((acc, row) => acc + row.ewbank, 0);
  return sum / matches.length;
};

// Grade validation helpers
const isVGrade = (grade) => {
  // Match VB grades and regular V-grades, including split grades
  return /^(?:VB|V\d{1,2})[\+-]?(?:\/(?:VB|V\d{1,2})[\+-]?)?$/.test(grade);
};

const isFontGrade = (grade) => {
  // Handle numeric-only font grades (4+, 5+)
  if (/^[0-9]\+?$/.test(grade)) {
    return true;
  }
  // Handle standard Font boulder grades with uppercase letters
  if (/^[0-9][ABC]\+?$/.test(grade)) {
    return true;
  }
  // Handle grades with fb prefix (only prefix is case insensitive)
  if (/^fb(?:[0-9]\+?|[0-9][ABC]\+?)$/i.test(grade)) {
    return true;
  }
  return false;
};

export const isYDSGrade = (grade) => {
  // Handle single grades: 5.9, 5.10a, 5.11-, 5.12+
  const singleGradePattern = /^5\.(?:[0-9]|1[0-5])(?:[\+-]|[abcd])?(?:\s+[RX])?$/;
  
  // Handle slash grades within same level: 5.11b/c, 5.11a/b
  const sameLevelSlashPattern = /^5\.(?:[0-9]|1[0-5])[abcd]\/[abcd](?:\s+[RX])?$/;
  
  // Handle cross-level slash grades: 5.11d/12a, 5.10d/11a
  const crossLevelSlashPattern = /^5\.(?:[0-9]|1[0-5])(?:[\+-]|[abcd])?\/(?:[0-9]|1[0-5])[abcd]?(?:\s+[RX])?$/;
  
  return singleGradePattern.test(grade) || 
         sameLevelSlashPattern.test(grade) || 
         crossLevelSlashPattern.test(grade);
};

export const isFrenchGrade = (grade) => {
  return /^[0-9][a-c]\+*((\/| to )[0-9]*[a-c]*\+*)*$/.test(grade);
};

export const isUIAAGrade = (grade) => {
  return /^(1|2|3|4|5|6|7|8|9|10|11|12)[\+-]*((\/| to )(1|2|3|4|5|6|7|8|9|10|11|12)*[\+-]*)*$/.test(grade);
};

const convertToPureYDS = (grade) => {
  if (!grade.includes('+') && !grade.includes('-')) return grade;

  const [prefix, num] = grade.split('.');
  const cleanNum = num.replace(/[+-]/, ''); // Clean the number part before using it
  const gradeNum = parseInt(cleanNum);
  
  // For 5.9 and below, just remove the +/-
  if (gradeNum <= 9) {
    return `${prefix}.${cleanNum}`;
  }
  
  if (baseGrade.includes('-')) {
    return `${prefix}.${cleanNum}a`;
  }
  
  if (baseGrade.includes('+')) {
    return `${prefix}.${cleanNum}b/c`;
  }
  
  return grade;
};

export const convertToEwbanks = (grade) => {
  if (!grade) return NaN;
  
  // // console.debug('Converting grade:', grade);
  
  // Handle direct Ewbank slash grades like "20/21"
  if (/^\d+\/\d+$/.test(grade)) {
    const [first, second] = grade.split('/').map(g => parseInt(g, 10));
    if (isValidEwbankGrade(first) && isValidEwbankGrade(second)) {
      return (first + second) / 2;
    }
  }
  
  // Handle regular Ewbank integer grades
  if (/^[0-9]+$/.test(grade)) {
    const num = parseInt(grade);
    return isValidEwbankGrade(num) ? num : NaN;
  }
  
  if (isYDSGrade(grade)) {
    // Strip R/X safety ratings up front so they don't trip slash splitting or table lookups.
    grade = grade.replace(/\s+[RX]$/, '');
    const pureGrade = convertToPureYDS(grade);

    // Handle slash grades
    if (grade.includes('/')) {
      const parts = grade.split('/');
      let firstGrade = parts[0];
      let secondGrade = parts[1];
      
      // Determine if this is a same-level or cross-level slash grade
      if (!secondGrade.includes('.')) {
        // Check if the second part is just a letter (same-level) or a number+letter (cross-level)
        if (/^[abcd]$/.test(secondGrade)) {
          // Same level - like "5.10c/d" -> extract base from first grade
          const baseMatch = firstGrade.match(/^(5\.\d+)/);
          if (baseMatch) {
            secondGrade = baseMatch[1] + secondGrade;
          }
        } else if (/^\d+[abcd]?/.test(secondGrade)) {
          // Cross level - like "5.11d/12a" -> add "5." prefix
          secondGrade = '5.' + secondGrade;
        }
      }
      
      // console.debug('Looking up slash grades:', firstGrade, secondGrade);
      const matches1 = findGradeByYDS(firstGrade);
      const matches2 = findGradeByYDS(secondGrade);
      // console.debug('Slash grade matches:', matches1, matches2);
      
      // Only calculate average if both grades found matches
      if (matches1.length > 0 && matches2.length > 0) {
        return getAverageEwbank([...matches1, ...matches2]);
      }
      
      // Try converting with modifiers if direct lookup failed
      const convertedFirst = convertToPureYDS(firstGrade);
      const convertedSecond = convertToPureYDS(secondGrade);
      
      if (convertedFirst !== firstGrade || convertedSecond !== secondGrade) {
        // console.debug('Retrying with converted grades:', convertedFirst, convertedSecond);
        const convertedMatches1 = findGradeByYDS(convertedFirst);
        const convertedMatches2 = findGradeByYDS(convertedSecond);
        return getAverageEwbank([...convertedMatches1, ...convertedMatches2]);
      }
      
      return NaN;
    }
    
    // Standard grade lookup
    // console.debug('Looking up pure grade:', pureGrade);
    const matches = findGradeByYDS(pureGrade);
    // console.debug('Pure grade matches:', matches);
    if (!matches || matches.length === 0) {
      // console.debug('No matches found in grade data for:', pureGrade);
    }
    return getAverageEwbank(matches);
  }
  
  if (isFrenchGrade(grade)) {
    // Handle French slash grades like "6a/a+", "6a/b", "7a/a+"
    if (grade.includes('/')) {
      const parts = grade.split('/');
      let firstGrade = parts[0];
      let secondGrade = parts[1];

      // If second part doesn't have a number, inherit from first grade
      // e.g., "6a/a+" -> "6a" and "6a+"
      // e.g., "6a/b" -> "6a" and "6b"
      if (!/^[0-9]/.test(secondGrade)) {
        const baseMatch = firstGrade.match(/^([0-9]+)/);
        if (baseMatch) {
          secondGrade = baseMatch[1] + secondGrade;
        }
      }

      const matches1 = findGradeByFrench(firstGrade);
      const matches2 = findGradeByFrench(secondGrade);

      if (matches1.length > 0 && matches2.length > 0) {
        return getAverageEwbank([...matches1, ...matches2]);
      }
      // Fall through to try exact match if split lookup failed
    }

    const matches = findGradeByFrench(grade);
    return getAverageEwbank(matches);
  }
  
  if (isUIAAGrade(grade)) {
    const matches = findGradeByUIAA(grade);
    return getAverageEwbank(matches);
  }
  
  // console.debug('Unrecognized grade format:', grade);
  return NaN;
};

// Helper function to check if a number is a valid Ewbank grade
const isValidEwbankGrade = (num) => {
  return Number.isInteger(num) && num > 0 && num < 39;
};

// Cache the grade table since it's constant
const fractionalGradeTable = generateFractionalGradeTable();

export const convertToVGrade = (grade) => {
  if (!grade) return null;
  
  // If it's already a V-grade
  if (isVGrade(grade)) {
    // Handle split grades by averaging
    if (grade.includes('/')) {
      const [first, second] = grade.split('/');
      const firstNum = vGradeToNumber(first);
      const secondNum = vGradeToNumber(second);
      if (firstNum !== null && secondNum !== null) {
        return (firstNum + secondNum) / 2;
      }
    }
    
    // Single grade
    return vGradeToNumber(grade);
  }

  // Font to V-grade conversion using the grade table
  if (isFontGrade(grade)) {
    const fontGrade = grade.replace(/^fb/i, '');
    return findAverageVGrade(fontGrade, fractionalGradeTable);
  }

  return null;
};


// Grade formatting
export const formatGrade = (grade, system, climbingStyle) => {
  if (climbingStyle === CLIMBING_STYLES.BOULDER) {
    return `V${grade}`;
  }
  
  // Existing grade formatting logic
  switch (system) {
    case 'french':
      return convertEwbanksToFrench(grade);
    case 'yds':
      return convertEwbanksToYDS(grade);
    default:
      return grade;
  }
};

// Optional: Export a default object with all utils for backwards compatibility
export default {
  SPORT_CLEAN_ASCENT_TYPES,
  BOULDER_CLEAN_ASCENT_TYPES,
  FAILED_ASCENT_TYPES,
  french_grades,
  yds_grades,
  customColors,
  isVGrade,
  isFontGrade,
  convertToVGrade,
  formatGrade,
  getAscentTypeColor
};
